<template>
  <div class="orgs-page py-3">
    <header class="orgs-toolbar">
      <div class="orgs-toolbar__title">
        <span class="orgs-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Organisations</h1>
          <p>View, create, and rename the organisations that staff and orders belong to</p>
        </div>
      </div>
      <div class="orgs-toolbar__actions">
        <span v-if="lastUpdatedAt && !isLoading" class="orgs-updated">
          Updated {{ lastUpdatedLabel }}
        </span>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadData"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
        <CButton
          color="primary"
          size="sm"
          class="create-org-btn"
          @click="goToCreate"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="create-org-btn__icon" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>Create Organisation</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="orgs-alert">{{ errorMessage }}</CAlert>

    <div class="summary-tiles">
      <div class="summary-tile summary-tile--total">
        <span class="summary-tile__label">Total Organisations</span>
        <span class="summary-tile__value">{{ organisations.length }}</span>
      </div>
    </div>

    <BaseTable
      :items="filteredOrganisations"
      :columns="columns"
      title="All Organisations"
      accent="linear-gradient(180deg, #0e5b46, #16a07a)"
      :loading="isLoading"
      :default-sort="{ column: 'id', direction: 'asc' }"
      striped
      hover
      bordered
      sticky-header
      class="orgs-table"
    >
      <template #toolbar>
        <div class="orgs-search-wrap">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="orgs-search-icon" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model.trim="searchTerm"
            type="text"
            class="orgs-input"
            placeholder="Search organisations…"
          />
          <button
            v-if="searchTerm"
            type="button"
            class="orgs-search-clear"
            aria-label="Clear search"
            @click="searchTerm = ''"
          >×</button>
        </div>
      </template>

      <template #cell-name="{ row }">
        <div class="org-name">
          <span
            v-if="row.logo_url"
            class="org-avatar org-avatar--image"
          >
            <img :src="row.logo_url" :alt="row.name" class="org-avatar__img" @error="onAvatarError(row)" />
          </span>
          <span v-else class="org-avatar" :style="avatarStyle(row.name)">{{ initials(row.name) }}</span>
          <span class="org-name__text">{{ row.name }}</span>
        </div>
      </template>

      <template #cell-actions="{ row }">
        <button type="button" class="edit-btn" @click="goToEdit(row.id)">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          <span>Edit</span>
        </button>
      </template>

      <template #empty>
        <div v-if="!organisations.length" class="orgs-empty">
          <div class="orgs-empty__title">No organisations yet</div>
          <div class="orgs-empty__hint">
            <button type="button" class="orgs-empty__link" @click="goToCreate">Create your first organisation</button> to get started.
          </div>
        </div>
        <div v-else class="orgs-empty">
          <div class="orgs-empty__title">No matches for "{{ searchTerm }}"</div>
          <div class="orgs-empty__hint">
            Try a different name, or
            <button type="button" class="orgs-empty__link" @click="searchTerm = ''">clear search</button>.
          </div>
        </div>
      </template>
    </BaseTable>
  </div>
</template>

<script>
import axios from 'axios'
import BaseTable from '@/components/BaseTable.vue'

const AVATAR_PALETTE = [
  ['#0e5b46', '#16a07a'],
  ['#1e3a8a', '#3b82f6'],
  ['#5b21b6', '#8b5cf6'],
  ['#92400e', '#f59e0b'],
  ['#115e59', '#14b8a6'],
  ['#7f1d1d', '#ef4444']
]

export default {
  name: 'SettingsOrganisations',
  components: { BaseTable },
  data () {
    return {
      isLoading: false,
      errorMessage: '',
      searchTerm: '',
      organisations: [],
      lastUpdatedAt: null,
      columns: [
        { key: 'id', label: 'Id', width: '64px', align: 'right' },
        { key: 'name', label: 'Organisation' },
        { key: 'actions', label: '', width: '88px', align: 'right', sortable: false }
      ]
    }
  },
  computed: {
    filteredOrganisations () {
      const term = this.searchTerm.toLowerCase()
      if (!term) return this.organisations
      return this.organisations.filter((org) =>
        [org.id, org.name]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
      )
    },
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return ''
      const hours = String(this.lastUpdatedAt.getHours()).padStart(2, '0')
      const minutes = String(this.lastUpdatedAt.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.isLoading = true
      this.errorMessage = ''

      const token = localStorage.getItem('api_token')

      axios.get(`${this.$apiAdress}/api/organisations?token=${token}`)
        .then((response) => {
          this.organisations = this.extractOrganisations(response.data)
          this.lastUpdatedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load organisations right now.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    extractOrganisations (data) {
      if (Array.isArray(data)) return data
      if (Array.isArray(data?.data)) return data.data
      if (Array.isArray(data?.organisations)) return data.organisations
      return []
    },
    initials (name) {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },
    avatarStyle (name) {
      const text = String(name || '')
      let hash = 0
      for (let i = 0; i < text.length; i += 1) {
        hash = (hash * 31 + text.charCodeAt(i)) >>> 0
      }
      const [from, to] = AVATAR_PALETTE[hash % AVATAR_PALETTE.length]
      return { background: `linear-gradient(135deg, ${from}, ${to})` }
    },
    goToCreate () {
      this.$router.push('/settings/organisations/create')
    },
    goToEdit (id) {
      this.$router.push(`/settings/organisations/${id}/edit`)
    },
    onAvatarError (row) {
      const idx = this.organisations.findIndex((org) => org.id === row.id)
      if (idx !== -1) {
        this.$set(this.organisations[idx], 'logo_url', '')
      }
    }
  }
}
</script>

<style scoped>
.orgs-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.orgs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.orgs-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.orgs-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.orgs-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.orgs-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.orgs-toolbar__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.orgs-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.orgs-toolbar__actions >>> .btn.create-org-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.orgs-toolbar__actions >>> .btn.create-org-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.orgs-toolbar__actions >>> .btn.create-org-btn:active {
  transform: translateY(0);
  filter: brightness(0.96);
}

.create-org-btn__icon {
  flex-shrink: 0;
  display: block;
}

.orgs-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.orgs-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.orgs-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.orgs-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.orgs-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes refresh-spin {
  to { transform: rotate(360deg); }
}

/* ── Alerts ── */
.orgs-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Summary tiles ── */
.summary-tiles {
  display: grid;
  grid-template-columns: minmax(0, 240px);
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  border-left-width: 4px;
  border-left-color: #1f2a44;
}

.summary-tile__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
}

.summary-tile__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2a44;
}

/* ── Table card ── */
.orgs-table {
  margin-bottom: 1rem;
}

/* ── Search ── */
.orgs-search-wrap {
  position: relative;
  width: 280px;
  max-width: 100%;
}

.orgs-search-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.orgs-input {
  width: 100%;
  height: 34px;
  padding: 0.3rem 2.25rem 0.3rem 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  background: #ffffff;
  font-size: 0.82rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.orgs-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.orgs-search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.orgs-search-clear:hover {
  background: #d1d5db;
  color: #1f2a44;
}

/* ── Empty state ── */
.orgs-empty {
  text-align: center;
  padding: 1.5rem 1rem;
}

.orgs-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

.orgs-empty__hint {
  font-size: 0.82rem;
  color: #6b7280;
}

.orgs-empty__link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.orgs-empty__link:hover {
  text-decoration: underline;
}

/* ── Cells ── */
.org-name {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
}

.org-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  flex-shrink: 0;
}

.org-avatar--image {
  background: #ffffff;
  border: 1px solid #e5e9f2;
}

.org-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.org-name__text {
  font-size: 0.88rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

/* ── Edit button ── */
.edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.edit-btn:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.1);
}

@media (max-width: 767px) {
  .orgs-toolbar {
    align-items: flex-start;
  }
  .orgs-search-wrap {
    width: 100%;
  }
  .orgs-toolbar__title {
    flex: 1 1 100%;
  }
  .orgs-toolbar__actions {
    flex: 1 1 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 575px) {
  .orgs-page {
    padding: 0 0 0.5rem;
  }
  .orgs-toolbar {
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .orgs-toolbar__accent {
    height: 30px;
  }
  .orgs-toolbar__title h1 {
    font-size: 1rem;
  }
  .orgs-toolbar__title p {
    font-size: 0.72rem;
  }
  .orgs-toolbar__actions {
    gap: 0.45rem;
  }
  .orgs-toolbar__actions >>> .btn.create-org-btn,
  .orgs-toolbar__actions >>> .btn.refresh-btn {
    height: 32px;
    font-size: 0.76rem;
    padding: 0 0.7rem;
  }
  .orgs-updated {
    flex: 1 1 100%;
    font-size: 0.7rem;
  }
}
</style>
